import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { CookieBanner } from "@/components/CookieBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { ScrollManager } from "@/components/ScrollManager";
import { SeoRouteReset } from "@/components/SeoRouteReset";
import { AuthProvider } from "@/contexts/AuthContext";
import { CityProvider } from "@/contexts/CityContext";
import { WaitlistProvider } from "@/contexts/WaitlistContext";
import { useAudience } from "@/hooks/useAudience";
import { usePostHogPageview } from "@/hooks/usePostHogPageview";
import Landing from "@/pages/Landing";
import { PostHogProvider } from "@/providers/PostHogProvider";
import { CITIES } from "@/seo/shared";
import { LANDING_SLUGS, landingPath } from "@/seo/landings";

// Code-split: Landing is eager (first paint on /), everything else lazy.
const Movies = lazy(() => import("@/pages/Movies"));
const MovieDetail = lazy(() => import("@/pages/MovieDetail"));
const LegacyMovieRedirect = lazy(() => import("@/pages/LegacyMovieRedirect"));
const Suscripcion = lazy(() => import("@/pages/Suscripcion"));
const Cinemas = lazy(() => import("@/pages/Cinemas"));
const CinemaDetail = lazy(() => import("@/pages/CinemaDetail"));
const Privacidad = lazy(() => import("@/pages/Privacidad"));
const Cookies = lazy(() => import("@/pages/Cookies"));
const AvisoLegal = lazy(() => import("@/pages/AvisoLegal"));
const Terminos = lazy(() => import("@/pages/Terminos"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Login = lazy(() => import("@/pages/Login"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const Healthcheck = lazy(() => import("@/pages/Healthcheck"));
const InternalEconomics = lazy(() => import("@/pages/InternalEconomics"));
const CinemaSimulations = lazy(() => import("@/pages/CinemaSimulations"));

const queryClient = new QueryClient();

// Must live inside BrowserRouter so the hooks can read useLocation.
// PostHog (solo con consentimiento) + medición sin cookies (src/lib/audience.ts).
function RouterAnalytics() {
  usePostHogPageview();
  useAudience();
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <PostHogProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <RouterAnalytics />
              <SeoRouteReset />
              <ScrollManager />
              <CityProvider>
              <WaitlistProvider>
                <Suspense fallback={null}>
                  <Routes>
                    {/* Public surfaces wrapped in header + footer. Landing keeps
                        the header transparent (hero behind it); interior pages
                        fall back to the solid-on-scroll behaviour as soon as
                        the user scrolls. */}
                    <Route element={<PublicLayout />}>
                      <Route path="/" element={<Landing />} />
                      <Route path="/suscripcion" element={<Suscripcion />} />
                    </Route>
                    <Route element={<PublicLayout headerTransparent={false} />}>
                      <Route path="/peliculas" element={<Movies />} />
                      {/* Ficha por ciudad (URL indexable) + URL antigua por
                          UUID, que redirige. Vercel sirve estas rutas con
                          HTML prerenderizado / 301 (ver vercel.json). */}
                      {CITIES.map((c) => (
                        <Route
                          key={c.slug}
                          path={`/${c.slug}/pelicula/:slug`}
                          element={<MovieDetail citySlug={c.slug} />}
                        />
                      ))}
                      <Route path="/peliculas/:id" element={<LegacyMovieRedirect />} />
                      {/* Cartelera por ciudad: /barcelona, /valencia. */}
                      {CITIES.map((c) => (
                        <Route key={`city-${c.slug}`} path={`/${c.slug}`} element={<Movies city={c} />} />
                      ))}
                      {/* Landings por momento: /{ciudad}/hoy, /fin-de-semana, /version-original,
                          /sesiones-especiales (src/seo/landings.ts; también en vercel.json). */}
                      {CITIES.flatMap((c) =>
                        LANDING_SLUGS.map((l) => (
                          <Route key={`${c.slug}-${l}`} path={landingPath(c.slug, l)} element={<Movies city={c} landing={l} />} />
                        ))
                      )}
                      <Route path="/cines" element={<Cinemas />} />
                      <Route path="/cines/:slug" element={<CinemaDetail />} />
                      <Route path="/privacidad" element={<Privacidad />} />
                      <Route path="/cookies" element={<Cookies />} />
                      <Route path="/aviso-legal" element={<AvisoLegal />} />
                      <Route path="/terminos" element={<Terminos />} />
                      <Route path="/simulaciones" element={<InternalEconomics />} />
                      <Route
                        path="/simulaciones-cines"
                        element={<CinemaSimulations />}
                      />
                      <Route
                        path="/_internal/economics"
                        element={<Navigate to="/simulaciones" replace />}
                      />
                      {/* Catch-all 404 con cabecera + footer. */}
                      <Route path="*" element={<NotFound />} />
                    </Route>

                    {/* Dev / auth surfaces — no layout. */}
                    <Route path="/healthcheck" element={<Healthcheck />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                  </Routes>
                </Suspense>
                <CookieBanner />
              </WaitlistProvider>
              </CityProvider>
            </BrowserRouter>
          </AuthProvider>
        </QueryClientProvider>
      </PostHogProvider>
    </ErrorBoundary>
  );
}
